import * as React from 'react';
import { Project } from '@models/project.model';
import { Timeline } from '../TimeLine';
import { Event } from '@models/event.model';
import { ChevronDownIcon } from 'lucide-react';
import { Task } from '@models/task.model';
import { getProjectAccordionExpansionConfig } from './utils/getProjectAccordionExpansionConfig';
import { Navigation } from './components/Navigation';
import { getProjectStateColor } from '@utils/getProjectStateColor';
import { useAiPlanningAvailable } from '@hooks/api/projectPlanDrafts';
import { useTranslation } from '@hooks/useTranslation';

interface AccordionUsageProps {
  flag?: boolean;
  tasks?: Task[];
  events: Event[];
  projects: Project[];
  setTask: (id: string) => void;
  setTaskDetails?: (id: string) => void;
  projectIdToExpand?: string | null;
  setEvent: (id: string) => void;
  toggleNewTaskModal: (projectId: string) => void;
  toggleNewEventModal: (projectId: string) => void;
  toggleCreateTemplateModal?: (projectId: string) => void;
  toggleImportTemplateModal?: (projectId: string) => void;
  userRole?: string | null;
}

const AccordionUsage: React.FC<AccordionUsageProps> = ({
  tasks,
  events,
  setTask,
  setTaskDetails,
  setEvent,
  projects,
  flag = false,
  projectIdToExpand,
  toggleNewTaskModal,
  toggleNewEventModal,
  toggleCreateTemplateModal,
  toggleImportTemplateModal,
  userRole,
}) => {
  const [expandedItems, setExpandedItems] = React.useState<Set<string>>(
    new Set(),
  );
  const timelineRefs = React.useRef<{ [key: string]: any }>({});
  const { data: aiPlanningAvailable } = useAiPlanningAvailable();
  const tSchedule = useTranslation('schedule');
  const viewLabels = {
    project: tSchedule.viewProject,
    month: tSchedule.viewMonth,
    monthTooltip: tSchedule.viewMonthTooltip,
    group: tSchedule.viewRangeLabel,
  };

  React.useEffect(() => {
    const { shouldExpand, projectExists, elementId } =
      getProjectAccordionExpansionConfig(projectIdToExpand, projects);

    if (shouldExpand && projectExists && projectIdToExpand && elementId) {
      setExpandedItems(new Set([projectIdToExpand]));
    }
  }, [projectIdToExpand, projects]);

  const toggleAccordion = (projectId: string) => {
    const newExpandedItems = new Set(expandedItems);
    if (newExpandedItems.has(projectId)) {
      newExpandedItems.delete(projectId);
    } else {
      newExpandedItems.add(projectId);
    }
    setExpandedItems(newExpandedItems);
  };

  const getDisplayProjectStatus = (status: string) => {
    if (status === 'COMPLETED') {
      return 'Completed';
    }
    if (status === 'ONGOING') {
      return 'Active';
    }
    if (status === 'ABANDONED') {
      return 'Abandoned';
    }
    return status;
  };

  return (
    <div className="space-y-4">
      {projects.map((project: any, index: number) => {
        const isExpanded = expandedItems.has(project.projectId);
        const isLast = index === projects.length - 1;
        if (projectIdToExpand && project.projectId !== projectIdToExpand) {
          return null;
        }

        return (
          <div
            key={project.projectId}
            id={`accordion-button-${project.projectId}`}
            className="rounded-lg overflow-hidden">
            <div className="cursor-pointer">
              <div
                className={`flex items-center justify-between w-full p-4 font-medium text-left text-gray-700 bg-gray-100 hover:bg-gray-200 focus:ring-4 focus:ring-blue-200 transition-colors duration-200 ${
                  isLast ? '' : 'border-b border-gray-200'
                }`}
                onClick={() => toggleAccordion(project.projectId)}
                aria-expanded={isExpanded}
                aria-controls={`accordion-body-${project.projectId}`}>
                <div className="flex flex-col items-start gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-base font-semibold text-blue-800 capitalize">
                      {project.originalProjectId &&
                      project.originalProject?.organization?.name
                        ? `${project.name} (from: ${project.originalProject.organization.name})`
                        : project.name}
                    </span>
                    <div
                      className={`inline-flex items-center justify-center rounded-full font-medium whitespace-nowrap w-fit px-2 py-0.5 text-xs h-5 ${getProjectStateColor(project.state)} text-white`}>
                      {getDisplayProjectStatus(project.state)}
                    </div>
                  </div>
                  <span className="text-sm font-normal text-gray-600">
                    Address: {project.address}
                  </span>
                </div>
                <ChevronDownIcon
                  className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                />
              </div>
            </div>
            <div
              id={`accordion-body-${project.projectId}`}
              className={`transition-all duration-200 ease-in-out ${
                isExpanded ? 'block' : 'hidden'
              }`}
              aria-labelledby={`accordion-heading-${project.projectId}`}>
              <div className="p-0 m-0 min-h-0 overflow-visible">
                {/* Navigation Component */}
                <Navigation
                  projectId={project.projectId}
                  timelineRefs={timelineRefs}
                  toggleNewTaskModal={toggleNewTaskModal}
                  toggleNewEventModal={toggleNewEventModal}
                  toggleCreateTemplateModal={toggleCreateTemplateModal}
                  toggleImportTemplateModal={toggleImportTemplateModal}
                  isDelegated={project.originalProjectId ? true : false}
                  userRole={userRole}
                  aiPlanningAvailable={aiPlanningAvailable}
                  viewLabels={viewLabels}
                />

                <Timeline
                  ref={(ref) => {
                    if (ref) {
                      timelineRefs.current[project.projectId] = ref;
                    }
                  }}
                  flag={flag}
                  events={events}
                  tasks={tasks}
                  setTask={setTask}
                  setTaskDetails={setTaskDetails}
                  setEvent={setEvent}
                  projectId={project?.projectId}
                  projectStartDate={project?.startDate}
                  projectEndDate={project?.endDate}
                  isDelegated={project.originalProjectId ? true : false}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AccordionUsage;
